<template>
     <div>
        <main-navigation></main-navigation>

  <div class="profile-page">
    <div class="container">
      
      <!-- Profile Card -->
      <div class="profile-card">
        
        <!-- Header -->
        <div class="profile-header">
          <div class="avatar-wrapper">
            <div class="avatar">
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </div>
          </div>
          <div class="header-info">
            <h2>{{ profile.Business_name || 'Business Profile' }}</h2>
            <p class="business-type">{{ profile.paybill_type || 'Payment Type Not Set' }}</p>
          </div>
          <div class="header-actions">
            <router-link to="/update/profile" class="edit-btn">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M20 14.66V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5.34"></path>
                <polygon points="18 2 22 6 12 16 8 16 8 12 18 2"></polygon>
              </svg>
              Edit Profile
            </router-link>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loadingProfile" class="loading-state">
          <div class="spinner"></div>
          <p>Loading profile...</p>
        </div>

        <!-- Profile Content -->
        <div v-else-if="hasProfile" class="profile-content">
          
          <!-- Business Information Section -->
          <div class="info-section">
            <h3 class="section-title">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="3" y1="9" x2="21" y2="9"></line>
                <line x1="9" y1="21" x2="9" y2="9"></line>
              </svg>
              Business Information
            </h3>
            <div class="info-grid">
              <div class="info-item">
                <label>Business Name</label>
                <div class="info-value">
                  <span>{{ profile.Business_name || 'Not set' }}</span>
                </div>
              </div>
              <div class="info-item">
                <label>Business Phone</label>
                <div class="info-value">
                  <span>{{ profile.Business_phone || 'Not set' }}</span>
                </div>
              </div>
              <div class="info-item full-width">
                <label>Business Address</label>
                <div class="info-value">
                  <span>{{ profile.Business_address || 'Not set' }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Payment Settings Section -->
          <div class="info-section">
            <h3 class="section-title">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"></path>
              </svg>
              Payment Settings
            </h3>
            <div class="info-grid">
              <div class="info-item">
                <label>Paybill Type</label>
                <div class="info-value">
                  <span class="payment-badge" :class="getPaymentTypeClass()">
                    {{ profile.paybill_type || 'Not configured' }}
                  </span>
                </div>
              </div>
              
              <!-- Show different fields based on paybill type -->
              <template v-if="profile.paybill_type === 'Bank Paybill'">
                <div class="info-item">
                  <label>Paybill Number</label>
                  <div class="info-value">
                    <code>{{ getPaybillNumber() }}</code>
                  </div>
                </div>
                <div class="info-item">
                  <label>Account Number</label>
                  <div class="info-value">
                    <code>{{ getAccountNumber() }}</code>
                  </div>
                </div>
              </template>
              
              <template v-else-if="profile.paybill_type === 'Safaricom Paybill'">
                <div class="info-item full-width">
                  <label>Paybill Number</label>
                  <div class="info-value">
                    <code>{{ profile.BusinessShortCode || 'Not set' }}</code>
                  </div>
                </div>
              </template>
              
              <template v-else-if="profile.paybill_type === 'Safaricom Till'">
                <div class="info-item full-width">
                  <label>Till Number</label>
                  <div class="info-value">
                    <code>{{ profile.BusinessShortCode || 'Not set' }}</code>
                  </div>
                </div>
              </template>
              
              <template v-else-if="profile.paybill_type === 'Pochi'">
                <div class="info-item full-width">
                  <label>Pochi Number</label>
                  <div class="info-value">
                    <code>{{ profile.BusinessShortCode || 'Not set' }}</code>
                  </div>
                </div>
              </template>
              
              <template v-else-if="profile.paybill_type === 'Phone Number'">
                <div class="info-item full-width">
                  <label>Phone Number</label>
                  <div class="info-value">
                    <code>{{ profile.BusinessShortCode || 'Not set' }}</code>
                  </div>
                </div>
              </template>
              
              <div class="info-item full-width" v-if="profile.paybill_type === 'Bank Paybill' && profile.account">
                <label>Business Account</label>
                <div class="info-value">
                  <code>{{ profile.account }}</code>
                </div>
              </div>
            </div>
          </div>

          <!-- Payment Instructions Preview -->
          <div class="preview-section" v-if="hasPaymentDetails">
            <h3 class="section-title">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
              How Your Tenants Will Pay
            </h3>
            <div class="preview-box">
              <div class="preview-content">
                <p><strong>Payment Instruction Preview:</strong></p>
                <code class="payment-preview">{{ getPaymentPreview() }}</code>
              </div>
            </div>
          </div>

        </div>

        <!-- No Profile State -->
        <div v-else class="empty-state">
          <div class="empty-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </div>
          <h3>No Profile Found</h3>
          <p>You haven't set up your business profile yet.</p>
          <router-link to="/update/profile" class="create-btn">
            Create Profile
          </router-link>
        </div>

      </div>
    </div>
      </div>
  </div>
</template>

<script>
import { apiFetch } from "@/services/api.js";

export default {
  name: "Profile",

  data() {
    return {
      loadingProfile: false,
      profile: {
        Business_name: "",
        Business_address: "",
        Business_phone: "",
        BusinessShortCode: "",
        paybill_type: "",
        account: ""
      }
    };
  },

  computed: {
    hasProfile() {
      return this.profile && this.profile.Business_name;
    },
    
    hasPaymentDetails() {
      return this.profile.paybill_type && this.profile.BusinessShortCode;
    }
  },

  async mounted() {
    await this.fetchProfile();
  },

  methods: {
    async fetchProfile() {
      try {
        this.loadingProfile = true;
        
        console.log("Fetching profile...");
        const data = await apiFetch("/profile");
        
        console.log("Profile data received:", data);
        
        // Handle different response structures (same as updateprofile)
        const profile = data.data || data;
        
        // Populate profile
        this.profile = {
          Business_name: profile.Business_name || "",
          Business_address: profile.Business_address || "",
          Business_phone: profile.Business_phone || "",
          BusinessShortCode: profile.BusinessShortCode || "",
          paybill_type: profile.paybill_type || "",
          account: profile.account || ""
        };
        
        console.log("Profile loaded:", this.profile);
        
      } catch (error) {
        console.error("Failed to load profile:", error);
      } finally {
        this.loadingProfile = false;
      }
    },
    
    getPaymentTypeClass() {
      const types = {
        'Bank Paybill': 'badge-bank',
        'Safaricom Paybill': 'badge-safaricom',
        'Safaricom Till': 'badge-till',
        'Pochi': 'badge-pochi',
        'Phone Number': 'badge-phone'
      };
      return types[this.profile.paybill_type] || 'badge-default';
    },
    
    getPaybillNumber() {
      if (this.profile.BusinessShortCode && this.profile.BusinessShortCode.includes('-')) {
        return this.profile.BusinessShortCode.split('-')[0];
      }
      return this.profile.BusinessShortCode || 'Not set';
    },
    
    getAccountNumber() {
      if (this.profile.BusinessShortCode && this.profile.BusinessShortCode.includes('-')) {
        return this.profile.BusinessShortCode.split('-')[1];
      }
      return this.profile.account || 'Not set';
    },
    
    getPaymentPreview() {
      const type = this.profile.paybill_type;
      const shortCode = this.profile.BusinessShortCode;
      
      if (type === 'Bank Paybill' && shortCode && shortCode.includes('-')) {
        const [paybill, acc] = shortCode.split('-');
        return `Paybill: ${paybill}\nAccount: ${acc} # [TENANT_ID]`;
      } else if (type === 'Safaricom Paybill') {
        return `Paybill: ${shortCode}\nAccount: [TENANT_ID]`;
      } else if (type === 'Safaricom Till') {
        return `Till Number: ${shortCode}\nReference: [TENANT_ID]`;
      } else if (type === 'Pochi') {
        return `Pochi Number: ${shortCode}\nReference: [TENANT_ID]`;
      } else if (type === 'Phone Number') {
        return `Send payment to: ${shortCode}\nReference: [TENANT_ID]`;
      }
      return 'Payment instructions not configured yet';
    }
  }
};
</script>

<style scoped>
.profile-page {
  background: var(--dash-bg);
  min-height: 100vh;
  padding: 40px 20px;
}

.container {
  max-width: 900px;
  margin: 0 auto;
}

/* Profile Card */
.profile-card {
  background: white;
  border-radius: var(--dash-radius-lg);
  box-shadow: var(--dash-shadow-lg);
  overflow: hidden;
  animation: fadeInUp 0.5s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Header */
.profile-header {
  background: var(--dash-primary);
  padding: 30px;
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.avatar-wrapper {
  flex-shrink: 0;
}

.avatar {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.avatar svg {
  color: white;
}

.header-info {
  flex: 1;
}

.header-info h2 {
  margin: 0 0 5px 0;
  color: white;
  font-size: 24px;
  font-weight: 600;
}

.business-type {
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
}

.header-actions {
  flex-shrink: 0;
}

.edit-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: white;
  color: var(--dash-primary);
  text-decoration: none;
  border-radius: var(--dash-radius-sm);
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s ease;
}

.edit-btn:hover {
  transform: translateY(-2px);
  box-shadow: var(--dash-shadow);
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 60px 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--dash-line);
  border-top-color: var(--dash-primary);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  margin: 0 auto 15px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Profile Content */
.profile-content {
  padding: 30px;
}

/* Info Sections */
.info-section {
  margin-bottom: 30px;
  padding-bottom: 30px;
  border-bottom: 1px solid var(--dash-line);
}

.info-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 20px 0;
  color: var(--dash-text);
  font-size: 18px;
  font-weight: 600;
}

.section-title svg {
  color: var(--dash-primary);
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.info-item.full-width {
  grid-column: span 2;
}

.info-item label {
  display: block;
  font-size: 12px;
  color: var(--dash-faint);
  margin-bottom: 5px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 16px;
  color: var(--dash-text);
  word-break: break-word;
}

.info-value code {
  background: var(--dash-bg-alt);
  padding: 4px 8px;
  border-radius: var(--dash-radius-xs);
  font-family: var(--dash-font-mono);
  font-size: 14px;
}

/* Payment Badges */
.payment-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: var(--dash-radius-lg);
  font-size: 12px;
  font-weight: 600;
}

.badge-bank {
  background: var(--dash-primary-soft);
  color: var(--dash-primary-ink);
}

.badge-safaricom {
  background: var(--dash-success-soft);
  color: var(--dash-success-ink);
}

.badge-till {
  background: var(--dash-warning-soft);
  color: var(--dash-warning-ink);
}

.badge-pochi {
  background: var(--dash-alt-soft);
  color: var(--dash-alt-ink);
}

.badge-phone {
  background: var(--dash-alt-soft);
  color: var(--dash-alt-ink);
}

.badge-default {
  background: var(--dash-bg-alt);
  color: var(--dash-text);
}

/* Preview Section */
.preview-section {
  margin-top: 30px;
  padding-top: 30px;
  border-top: 2px dashed var(--dash-line);
}

.preview-box {
  background: var(--dash-surface-2);
  border-radius: var(--dash-radius);
  padding: 20px;
  border-left: 4px solid var(--dash-primary);
}

.preview-content p {
  margin: 0 0 10px 0;
  font-size: 14px;
}

.payment-preview {
  display: block;
  background: var(--dash-ink);
  color: var(--dash-primary-line);
  padding: 15px;
  border-radius: var(--dash-radius-xs);
  font-family: var(--dash-font-mono);
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 30px;
}

.empty-icon svg {
  color: var(--dash-line-strong);
  margin-bottom: 20px;
}

.empty-state h3 {
  margin: 0 0 10px 0;
  color: var(--dash-text);
  font-size: 20px;
}

.empty-state p {
  margin: 0 0 25px 0;
  color: var(--dash-faint);
}

.create-btn {
  display: inline-block;
  padding: 12px 30px;
  background: var(--dash-primary);
  color: white;
  text-decoration: none;
  border-radius: var(--dash-pill);
  font-weight: 600;
  transition: all 0.3s ease;
}

.create-btn:hover {
  transform: translateY(-2px);
  box-shadow: var(--dash-shadow);
}

/* Responsive */
@media (max-width: 640px) {
  .profile-header {
    flex-direction: column;
    text-align: center;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .info-item.full-width {
    grid-column: span 1;
  }
  
  .profile-content {
    padding: 20px;
  }
}
</style>